import React, { useId, useState } from 'react';

/**
 * Ligatures off on description and FAQ text: Manrope's contextual alternates turn
 * "501(c)(3)" into "501©(3)" (Paper Handoff map bug fix; SPEC §C Type).
 */
export const NO_LIGATURES_CLASS = "[font-feature-settings:'calt'_0,'liga'_0]";

// Question type per size. `default` is 16/24 (the hosted page, the overlay's mobile layout);
// `compact` is 15/22, the body size of the text around it in a sheet, where the questions sit
// under a 15/22 description and answer at 15/22 (owner call 2026-10-21).
const QUESTION_TYPE = {
  default: 'text-[length:var(--cs-text-base)] leading-[var(--cs-leading-24)]',
  compact: 'text-[length:var(--cs-text-sm-plus)] leading-[var(--cs-leading-22)]',
};

function FAQRow({ faq, open, onToggle, size }) {
  const answerId = useId();
  return (
    <div className="flex flex-col border-b border-[color:var(--cs-border-control-subtle)]">
      <h3 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={answerId}
          onClick={onToggle}
          className={`flex w-full items-center justify-between gap-[var(--cs-space-3)] bg-transparent py-[var(--cs-space-4)] text-left ${QUESTION_TYPE[size] || QUESTION_TYPE.default} font-medium text-[color:var(--cs-text-primary)] hover:underline focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]`}
        >
          <span>{faq.question}</span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={`size-[var(--cs-icon-lg)] shrink-0 text-[color:var(--cs-text-tertiary)] [stroke-width:var(--cs-icon-stroke)] transition-transform duration-[var(--cs-motion-fast)] ${open ? 'rotate-180' : ''}`}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </h3>
      <div
        id={answerId}
        role="region"
        hidden={!open}
        className={`-mt-[var(--cs-space-3)] pb-[var(--cs-space-4)] text-[length:var(--cs-text-sm-plus)] leading-[var(--cs-leading-22)] text-[color:var(--cs-text-secondary)] whitespace-pre-line ${NO_LIGATURES_CLASS}`}
      >
        {faq.answer}
      </div>
    </div>
  );
}

/**
 * FAQRows — the quiet FAQ list (CON-518, Paper "FAQ row"): hairline rows, question 16/24
 * medium with a 20 chevron in the tertiary role, the open answer 15/22 in the secondary role
 * 4px below the question, ligatures off. One row open at a time, like the accordion it
 * replaces. Used by the hosted page, the overlay's mobile layout and the overlay FAQ drawer.
 *
 * @param {Array<{question: string, answer: string}>} faqs
 * @param {number|null} [defaultOpenIndex=null]
 * @param {'default'|'compact'} [size='default'] - `compact` sets questions at 15/22 (the sheets)
 */
function FAQRows({ faqs = [], defaultOpenIndex = null, size = 'default' }) {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return (
    <div className="flex flex-col">
      {faqs.map((faq, index) => (
        <FAQRow
          key={index}
          faq={faq}
          size={size}
          open={openIndex === index}
          onToggle={() => setOpenIndex((current) => (current === index ? null : index))}
        />
      ))}
    </div>
  );
}

export default FAQRows;
